import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../../../auth-lib';
import {
  Button, Card, StatusPill, Table, useToast,
} from '../../../design-system';
import '../../styles/TallyPanel.css';

// Linking ledgers in a client's books to clients the FIRM already has.
//
// ⚠️ THIS IS A SIDE ACTIVITY, NOT WHAT THE TAB IS FOR, and it used to be the
// whole tab. A client's Tally tab is about that client's books — the ledgers,
// the entries, the reports. This screen asks a different question: of the
// parties inside those books, are any of them somebody the firm also acts for?
// Useful, occasionally; not the reason anybody opens the tab. It now sits
// behind its own tab rather than being the first and only thing rendered,
// which is what made the whole feature read as "it thinks these are clients".
//
// ⚠️ THE TWO LEVELS, ONE MORE TIME, because conflating them has cost real
// damage here already:
//
//   a Tally COMPANY  →  a client of the firm      (the firm-level Tally page)
//   a LEDGER inside  →  a customer of THAT client (this screen)

const BASIS_LABEL = {
  gstin: { label: 'GSTIN match', variant: 'success' },
  pan: { label: 'PAN match', variant: 'success' },
  name: { label: 'Name guess', variant: 'warning' },
};

function TallyMatching({ connection, onChanged, onOpenLedger }) {
  const toast = useToast();
  const [matches, setMatches] = useState([]);
  const [parties, setParties] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [importing, setImporting] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, p] = await Promise.all([
        apiClient.get(`/api/tally/connections/${connection.id}/matches`),
        apiClient.get(`/api/tally/connections/${connection.id}/matches`, { params: { status: 'importable' } }),
      ]);
      setMatches(m.data?.matches || []);
      setParties(p.data?.matches || []);
      // ⚠️ NOTHING IS PRE-TICKED, and the reason is what pre-ticking did.
      // Party-group rows started selected on the theory that a debtor is
      // probably a client — true only in the firm's OWN books. In a client's
      // Tally those rows are that client's customers, and one press of Import
      // turned seven of them into client records of the firm, each consuming a
      // client code, none of them a client.
      setSelected(new Set());
    } catch {
      // Leave what is on screen rather than blanking the tab on a refresh blip.
    } finally {
      setLoading(false);
    }
  }, [connection.id]);

  useEffect(() => { load(); }, [load]);

  const decide = async (ledgerId, decision) => {
    try {
      await apiClient.patch(`/api/tally/connections/${connection.id}/matches/${ledgerId}`, { decision });
      setMatches((prev) => prev.filter((m) => m._id !== ledgerId));
    } catch (e) {
      toast.danger(e.response?.data?.error || 'Could not record that');
    }
  };

  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  /**
   * Create a Client from every ticked ledger in one call. The server dedupes
   * against clients the firm already has (so a party in its own books doesn't
   * become a twin) and reports what it did with each row.
   */
  const importParties = async () => {
    if (!selected.size) return;
    setImporting(true);
    try {
      const { data } = await apiClient.post(
        `/api/tally/connections/${connection.id}/import-parties`,
        { ledgerIds: [...selected] },
      );
      const bits = [];
      if (data.created?.length) bits.push(`${data.created.length} created`);
      if (data.linked?.length) bits.push(`${data.linked.length} linked to an existing client`);
      if (data.skipped?.length) bits.push(`${data.skipped.length} skipped`);
      if (data.failed?.length) bits.push(`${data.failed.length} failed`);
      const summary = bits.join(', ') || 'nothing to do';
      if (data.failed?.length) toast.warning(`Import finished — ${summary}`);
      else toast.success(`Import finished — ${summary}`);
      await load();
      onChanged?.();
    } catch (e) {
      toast.danger(e.response?.data?.error || 'Could not import those parties');
    } finally {
      setImporting(false);
    }
  };

  const isFirmBooks = connection.booksOwner === 'firm';

  return (
    <>
      <Card>
        <h3 className="tally-section-title">Ledgers that look like clients you already have</h3>
        <p className="tally-muted">
          Suggested from the books — confirm each one before anything relies on
          it. A wrong match would attribute this client&apos;s figures to another.
        </p>

        {loading && !matches.length ? (
          <p className="tally-muted">Loading…</p>
        ) : !matches.length ? (
          <p className="tally-muted">Nothing waiting for review.</p>
        ) : (
          <Table
            columns={[
              {
                key: 'name',
                header: 'Ledger in Tally',
                // ⚠️ THE NAME OPENS THE ACCOUNT, and on this screen that is not
                // a convenience. Deciding whether a ledger really is the client
                // you think it is means looking at what has gone through it —
                // and confirming a wrong match silently attributes one client's
                // figures to another. The evidence now sits one click away from
                // the decision instead of on a page nobody visits first.
                render: (m) => (
                  <button
                    type="button"
                    className="tally-linkish tally-linkish-inline"
                    onClick={() => onOpenLedger(m._id)}
                  >
                    {m.name}
                  </button>
                ),
              },
              { key: 'group', header: 'Group', render: (m) => m.parent || '—' },
              { key: 'client', header: 'Looks like', render: (m) => m.matchedClientId?.displayName || '—' },
              {
                key: 'basis',
                header: 'Because',
                render: (m) => {
                  const b = BASIS_LABEL[m.matchBasis];
                  return b ? <StatusPill variant={b.variant} label={b.label} /> : '—';
                },
              },
              {
                key: 'actions',
                header: '',
                render: (m) => (
                  <div className="tally-row-actions">
                    <Button size="sm" onClick={() => decide(m._id, 'confirm')}>Confirm</Button>
                    <Button size="sm" variant="secondary" onClick={() => decide(m._id, 'reject')}>
                      Not this
                    </Button>
                  </div>
                ),
              },
            ]}
            data={matches}
            keyField="_id"
          />
        )}
      </Card>

      <Card>
        <div className="tally-head">
          <div>
            <h3 className="tally-section-title">Parties not in your client list</h3>
            {/* ⚠️ WHAT THESE ROWS ARE DEPENDS ENTIRELY ON WHOSE BOOKS THEY CAME
                FROM, and saying so is the difference between a useful list and
                a trap. In a client's Tally they are that client's customers and
                suppliers — nothing to do with the firm's client book.

                ⚠️ THE LIST IS PARTY GROUPS ONLY NOW. It used to be every
                unmatched ledger, which is the whole chart of accounts: CGST,
                Bank Charges, Purchase Accounts and Profit & Loss A/c all
                appeared here under the word "parties". The server filters them
                through the matcher's own `isPartyGroup`. */}
            {isFirmBooks ? (
              <p className="tally-muted">
                Party ledgers in your firm&apos;s own books that match no client
                you already have. Importing creates a client from each — its
                name, plus PAN and GSTIN wherever Tally records them. Nothing is
                ticked to begin with.
              </p>
            ) : (
              <p className="tally-muted">
                These are <strong>{connection.companyName}</strong>&apos;s own
                customers and suppliers, not your clients — so they cannot be
                imported as client records. They are listed because matching them
                to clients you already have is what links this client&apos;s books
                to the rest of the system. If this Tally company is your
                firm&apos;s <em>own</em> books, change that on the connection and
                importing becomes available.
              </p>
            )}
          </div>
          {/* Hidden rather than disabled on a client's books: a greyed-out
              "Import as clients" invites the question "why can't I?", and the
              answer is that the action is wrong here, not unavailable. */}
          {isFirmBooks && (
            <div className="tally-actions">
              <Button onClick={importParties} loading={importing} disabled={importing || !selected.size}>
                {`Import ${selected.size} as client${selected.size === 1 ? '' : 's'}`}
              </Button>
            </div>
          )}
        </div>

        {loading && !parties.length ? (
          <p className="tally-muted">Loading…</p>
        ) : !parties.length ? (
          <p className="tally-muted">
            Every party ledger in these books is already matched to a client you have.
          </p>
        ) : (
          <Table
            columns={[
              ...(isFirmBooks ? [{
                key: 'sel',
                header: (
                  <input
                    type="checkbox"
                    aria-label="Select all"
                    checked={selected.size === parties.length && parties.length > 0}
                    ref={(el) => {
                      if (el) el.indeterminate = selected.size > 0 && selected.size < parties.length;
                    }}
                    onChange={(e) => setSelected(
                      e.target.checked ? new Set(parties.map((p) => p._id)) : new Set(),
                    )}
                  />
                ),
                render: (m) => (
                  <input
                    type="checkbox"
                    aria-label={`Import ${m.name}`}
                    checked={selected.has(m._id)}
                    onChange={() => toggle(m._id)}
                  />
                ),
              }] : []),
              {
                key: 'name',
                header: 'Ledger in Tally',
                // ⚠️ THE NAME OPENS THE ACCOUNT, and on this screen that is not
                // a convenience. Deciding whether a ledger really is the client
                // you think it is means looking at what has gone through it —
                // and confirming a wrong match silently attributes one client's
                // figures to another. The evidence now sits one click away from
                // the decision instead of on a page nobody visits first.
                render: (m) => (
                  <button
                    type="button"
                    className="tally-linkish tally-linkish-inline"
                    onClick={() => onOpenLedger(m._id)}
                  >
                    {m.name}
                  </button>
                ),
              },
              { key: 'group', header: 'Group', render: (m) => m.parent || '—' },
              { key: 'gstin', header: 'GSTIN', render: (m) => m.gstin || '—' },
              { key: 'pan', header: 'PAN', render: (m) => m.pan || '—' },
            ]}
            data={parties}
            keyField="_id"
          />
        )}
      </Card>
    </>
  );
}

export default TallyMatching;
